import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';
import { euro, fmtDate } from '../utils/formatUtils.js';
import { useAuth } from '../contexts/AuthContext';
import { sendDraftAgreementToClient } from '../utils/agreementUtils.js';
import { buildSepaMachtigingText } from '../utils/sepaMachtigingText.js';
import { buildNaamAdresVerklaringText } from '../utils/naamAdresVerklaringText.js';

// ─── Constanten ───────────────────────────────────────────────
const SESSION_CONFIG = {
  pt:          { label: '1-op-1 PT',            bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', badge: '1:1' },
  pt_plus:     { label: '1-op-2 PT',            bg: '#faf5ff', color: '#7e22ce', border: '#e9d5ff', badge: '1:2' },
  small_group: { label: 'Small Group Training', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', badge: 'SG' },
};
const SESSION_ORDER = ['pt', 'pt_plus', 'small_group'];

// Creditpool-opties voor custom regels (Onderdeel 1). 'pt_plus_60'/'pt_plus_30'
// zijn puur informatief (sessieduur) — beide muteren dezelfde
// current_pt_plus_credits-kolom, zie migratie 20260703100000.
const CUSTOM_CREDIT_POOLS = [
  { value: 'pt',          label: 'PT',                sessionType: 'pt' },
  { value: 'pt_plus_60',  label: 'PT-plus (60 min)',  sessionType: 'pt_plus' },
  { value: 'pt_plus_30',  label: 'PT-plus (30 min)',  sessionType: 'pt_plus' },
  { value: 'small_group', label: 'Small Group',       sessionType: 'small_group' },
];

// ─── Helper: beschikbare staffels filteren ────────────────────
function beschikbareStaffels(periods) {
  if (!periods) return [];
  const today = new Date().toISOString().slice(0, 10);
  return periods.filter(s =>
    s.is_active &&
    (!s.available_from  || s.available_from  <= today) &&
    (!s.available_until || s.available_until >= today)
  );
}

// ─── Helper: bonus-staffel matching op looptijd hoofdabonnement ───
// O1: SPIEGEL van de backend-matching in
// supabase/functions/sign-agreement/index.ts (blok "Kies de staffel
// waarvan duration_months het dichtst bij effectiveMonths ligt"):
//   1. exacte match op de looptijd van het hoofdabonnement wint;
//   2. anders de dichtstbijzijnde looptijd;
//   3. tie-break bij gelijke afstand: de kortste (staffels-array is
//      ASC gesorteerd op duration_months, dus de eerste blijft staan).
// Wijzig je deze regels? Wijzig dan óók de EF — en omgekeerd.
// hoofdMonths == null (looptijd nog niet gekozen, C/D stap 1): val terug
// op de goedkoopste staffel als indicatieve "vanaf"-prijs.
function matchBonusStaffel(bonus, hoofdMonths) {
  const staffels = bonus.staffels || [];
  if (staffels.length === 0) return null;
  if (hoofdMonths == null) {
    return staffels.reduce((min, s) =>
      (s.price_per_month ?? Infinity) < (min.price_per_month ?? Infinity) ? s : min
    , staffels[0]);
  }
  const exact = staffels.find(s => s.duration_months === hoofdMonths);
  if (exact) return exact;
  return staffels.reduce((best, s) => {
    const bestDiff = Math.abs((best?.duration_months ?? Infinity) - hoofdMonths);
    const curDiff  = Math.abs(s.duration_months - hoofdMonths);
    return curDiff < bestDiff ? s : best;
  }, null);
}

// ─── Helper: effectieve prijs + gratis-status van een bonus ───
// bonus_price_override: 0 = gratis, >0 = vaste prijs, null = gebruik
// de prijs van de op LOOPTIJD gematchte staffel (zelfde staffel als de
// backend factureert — niet meer de goedkoopste). De override bepaalt
// alleen het bedrag; de looptijd komt altijd uit de gematchte staffel
// (zie BRIEFING.md: override = bedrag, matching = looptijd).
function bonusEffectief(bonus, hoofdMonths = null) {
  const staffel = matchBonusStaffel(bonus, hoofdMonths);
  const months  = staffel?.duration_months ?? null;
  if (bonus.bonus_price_override === 0) {
    return { isFree: true, pricePerMonth: 0, months };
  }
  if (bonus.bonus_price_override != null && bonus.bonus_price_override > 0) {
    return { isFree: false, pricePerMonth: bonus.bonus_price_override, months };
  }
  if (!staffel) return { isFree: true, pricePerMonth: 0, months: null };
  const pm = staffel.price_per_month ?? 0;
  return { isFree: pm === 0, pricePerMonth: pm, months };
}

// ─── Helper: beste promo-label over alle actieve staffels ─────
function bestPromoLabel(periods) {
  const staffels = beschikbareStaffels(periods);
  const metPromo = staffels.filter(s => s.promo_label);
  if (metPromo.length === 0) return null;
  // Neem de staffel met de langste looptijd (meest grootschalige aanbieding)
  return metPromo.reduce((best, s) =>
    s.duration_months > best.duration_months ? s : best
  ).promo_label;
}

// ─── Helper: startpunt voor cliënt bepalen o.b.v. coach-draft ──
// Loopt de door de coach opgeslagen draft_selection langs (in volgorde,
// eerste element = hoofdabonnement) en zoekt het eerste type waarvoor
// nog een keuze ontbreekt: geen (geldige) staffel, of wel een staffel
// met payment_frequency='both' maar nog geen betaalfrequentie. Als alle
// types volledig geconfigureerd zijn: null (cliënt kan direct naar het
// overzicht, stap 3).
function resolveDraftStart(draft, types) {
  for (let i = 0; i < draft.length; i++) {
    const d = draft[i];
    const type = types.find(ty => ty.id === d.membership_type_id);
    const staffels = beschikbareStaffels(type?.membership_type_periods);
    const staffel = staffels.find(s => s.id === d.membership_type_period_id);
    if (!d.membership_type_period_id || !staffel) {
      return { configIdx: i, configSubStep: 'staffel' };
    }
    if (staffel.payment_frequency === 'both' && !d.payment_frequency) {
      return { configIdx: i, configSubStep: 'betaalfreq' };
    }
  }
  return null;
}

// ─── Helper: variabelen vervangen ─────────────────────────────
function replaceVariables(text, membershipType, tokenInfo, staffel) {
  if (!text) return t('overeenkomst.geenTekst');
  const naam     = tokenInfo?.client_name || '[naam]';
  const isCustom = tokenInfo?.is_custom || false;
  const abonnement = (isCustom && tokenInfo?.custom_name)
    ? tokenInfo.custom_name
    : (membershipType?.name || '[abonnement]');
  const effectivePrice = (isCustom && tokenInfo?.custom_price != null)
    ? tokenInfo.custom_price
    : staffel?.price_per_period;
  const prijs = effectivePrice != null ? euro(effectivePrice) : '[prijs]';
  const months = staffel?.duration_months;
  const looptijd = months ? `${months} ${months === 1 ? 'maand' : 'maanden'}` : '[looptijd]';
  const datum = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  return text
    .replace(/\[naam\]/g, naam)
    .replace(/\[abonnement\]/g, abonnement)
    .replace(/\[prijs\]/g, prijs)
    .replace(/\[looptijd\]/g, looptijd)
    .replace(/\[datum\]/g, datum)
    .replace(/\[bedrijfsnaam\]/g, 'Fit En Sterker');
}

// ─── Voortgangsindicator (stepper) ───────────────────────────
function Stepper({ labels, current }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '16px 24px 0',
      overflowX: 'auto',
      gap: 0,
    }}>
      {labels.map((label, idx) => {
        const stepNum = idx + 1;
        const isDone    = stepNum < current;
        const isActive  = stepNum === current;
        const isFuture  = stepNum > current;
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', flex: idx < labels.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                background: isDone ? '#1d9e75' : isActive ? '#1d9e75' : '#e5e7eb',
                color: isDone || isActive ? 'white' : '#9ca3af',
                flexShrink: 0,
              }}>
                {isDone ? '✓' : stepNum}
              </div>
              <div style={{
                fontSize: 10,
                marginTop: 4,
                color: isDone ? '#1d9e75' : isActive ? '#1d9e75' : '#9ca3af',
                fontWeight: isActive ? 700 : 400,
                whiteSpace: 'nowrap',
                textAlign: 'center',
              }}>
                {label}
              </div>
            </div>
            {idx < labels.length - 1 && (
              <div style={{
                flex: 1,
                height: 2,
                background: isDone ? '#1d9e75' : '#e5e7eb',
                margin: '0 4px',
                marginBottom: 16,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Sessie-type badge ─────────────────────────────────────────
function SessionBadge({ sessionType }) {
  const cfg = SESSION_CONFIG[sessionType] || { label: sessionType, bg: '#f3f4f6', color: '#374151', badge: '?' };
  return (
    <span style={{
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
    }}>
      {cfg.badge} {cfg.label}
    </span>
  );
}

// ─── Staffel kaart ────────────────────────────────────────────
function StaffelKaart({ staffel, selected, onClick }) {
  const discountPct   = Number(staffel.period_discount_pct || 0);
  const heeftKorting  = discountPct > 0 && ['period', 'both'].includes(staffel.payment_frequency);
  const kortingsBedrag = heeftKorting ? staffel.price_per_period * (discountPct / 100) : 0;
  const geprijsdPeriod = staffel.price_per_period - kortingsBedrag;

  return (
    <div
      onClick={onClick}
      style={{
        border: `2px solid ${selected ? '#1d9e75' : '#e5e7eb'}`,
        borderRadius: 10,
        padding: '16px 18px',
        cursor: 'pointer',
        background: selected ? '#f0fdf4' : 'white',
        transition: 'all 0.15s',
        position: 'relative',
      }}
    >
      {selected && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: '#1d9e75', color: 'white',
          borderRadius: '50%', width: 20, height: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700,
        }}>✓</div>
      )}
      {staffel.promo_label && (
        <div style={{
          display: 'inline-block',
          background: '#dcfce7',
          color: '#15803d',
          border: '1px solid #86efac',
          borderRadius: 6,
          padding: '3px 10px',
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 8,
        }}>
          🎁 {staffel.promo_label}
        </div>
      )}
      <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', marginBottom: 4 }}>
        {staffel.duration_months === 1 ? '1 maand' : `${staffel.duration_months} maanden`}
      </div>
      <div style={{ fontSize: 14, color: '#1d9e75', fontWeight: 600 }}>
        {euro(staffel.price_per_month || 0)}/mnd
      </div>
      {heeftKorting ? (
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' }}>{euro(staffel.price_per_period)}</span>
          <span style={{ fontSize: 13, color: '#15803d', fontWeight: 700 }}>{euro(geprijsdPeriod)}</span>
          <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '2px 7px', fontWeight: 600 }}>
            {t('staffelKaart.kortingLabel', { pct: discountPct })}
          </span>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          Totaal {euro(staffel.price_per_period)}
        </div>
      )}
    </div>
  );
}

// ─── Bonus chip ───────────────────────────────────────────────
function BonusChip({ name, isFree, pricePerMonth }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: '#f0fdf4',
      border: '1px solid #86efac',
      borderRadius: 20,
      padding: '4px 12px',
      fontSize: 12,
      color: '#15803d',
      fontWeight: 600,
    }}>
      <span>🎁</span>
      <span>{name}</span>
      {isFree
        ? <span style={{ background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }}>Gratis</span>
        : pricePerMonth != null && <span>{euro(pricePerMonth)}/mnd</span>
      }
    </div>
  );
}

// ─── Betaalfrequentie keuze ───────────────────────────────────
function BetaalfreqKeuze({ staffel, selectedFreq, onSelect }) {
  const discountPct    = Number(staffel.period_discount_pct || 0);
  const kortingsBedrag = discountPct > 0 ? staffel.price_per_period * (discountPct / 100) : 0;
  const geprijsdPeriod = staffel.price_per_period - kortingsBedrag;

  const opties = [
    {
      value: 'month',
      title: t('overeenkomstBetaalfreq.maandelijks'),
      sub: `${euro(staffel.price_per_month || 0)} ${t('overeenkomstBetaalfreq.maandelijksLabel')}`,
    },
    {
      value: 'period',
      title: discountPct > 0 ? t('overeenkomstBetaalfreq.eenmaligMetKorting') : t('overeenkomstBetaalfreq.eenmalig'),
      sub: discountPct > 0
        ? `${euro(geprijsdPeriod)} totaal (${discountPct}% ${t('overeenkomstBetaalfreq.kortingLabel')})`
        : `${euro(staffel.price_per_period)} totaal`,
      besparing: discountPct > 0 ? euro(kortingsBedrag) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {opties.map(opt => (
        <div
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          style={{
            border: `2px solid ${selectedFreq === opt.value ? '#1d9e75' : '#e5e7eb'}`,
            borderRadius: 10,
            padding: '14px 16px',
            cursor: 'pointer',
            background: selectedFreq === opt.value ? '#f0fdf4' : 'white',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%',
              border: `2px solid ${selectedFreq === opt.value ? '#1d9e75' : '#d1d5db'}`,
              background: selectedFreq === opt.value ? '#1d9e75' : 'white',
              flexShrink: 0,
            }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.title}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{opt.sub}</div>
              {opt.besparing && (
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
                  {t('overeenkomstBetaalfreq.kortingBesparing')}: {opt.besparing}
                </div>
              )}
              {opt.value === 'period' && discountPct > 0 && (
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 2, fontStyle: 'italic' }}>
                  {t('overeenkomstBetaalfreq.eenmaligKortingToelichting', { pct: discountPct })}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Overzicht item ───────────────────────────────────────────
function OverzichtItem({ naam, pricePerMonth, credits, months, paymentFreq, isBonus, isFree, sessionType, promoLabel, staffelAfwijking }) {
  const cfg = sessionType ? (SESSION_CONFIG[sessionType] || {}) : {};
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid #f3f4f6',
      gap: 12,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isBonus && <span>🎁</span>}
          <span style={{ fontWeight: isBonus ? 500 : 700, fontSize: 14 }}>{naam}</span>
          {sessionType && (
            <span style={{
              background: cfg.bg || '#f3f4f6',
              color: cfg.color || '#374151',
              borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 600,
            }}>
              {cfg.badge} {cfg.label}
            </span>
          )}
          {isFree && (
            <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
              GRATIS
            </span>
          )}
          {promoLabel && (
            <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
              {promoLabel}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>
          {months} {months === 1 ? 'maand' : 'maanden'} —{' '}
          {paymentFreq === 'month' ? t('overeenkomst.overzichtFreqMaand') : t('overeenkomst.overzichtFreqPeriode')}
          {credits != null && <>{' — '}{t('overeenkomst.creditsPerMaand', { n: credits })}</>}
        </div>
        {staffelAfwijking && (
          <div style={{ fontSize: 11, color: '#b45309', marginTop: 3, fontWeight: 600 }}>
            ⚠ {staffelAfwijking}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {isFree ? (
          <span style={{ fontWeight: 700, color: '#15803d' }}>Gratis</span>
        ) : pricePerMonth != null ? (
          <span style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 15 }}>{euro(pricePerMonth)}/mnd</span>
        ) : null}
      </div>
    </div>
  );
}

// ─── Wrapper: witte kaart op groene achtergrond ──────────────
function PageShell({ children, maxWidth = 720, title }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1d9e75 0%, #1a5c3a 100%)',
      padding: '40px 16px',
    }}>
      <div style={{
        maxWidth,
        margin: '0 auto',
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ background: '#1d9e75', padding: '24px 28px', color: 'white' }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{title || t('overeenkomst.titel')}</h1>
        </div>
        {children}
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .oe-type-card { position: relative; }
        .oe-exclude-btn { opacity: 0; transition: opacity 0.15s; }
        .oe-type-card:hover .oe-exclude-btn { opacity: 1; }
      `}</style>
    </div>
  );
}

// ─── Terug-knop ───────────────────────────────────────────────
function BackBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#6b7280', fontSize: 13, padding: '4px 0', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20,
      }}
    >
      ← {t('overeenkomst.stepTerug')}
    </button>
  );
}

// ─── Hoofd component ─────────────────────────────────────────
export default function OvereenkomstPage() {
  const { token, clientId } = useParams();
  const coachMode = !!clientId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef(null);

  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [tokenData, setTokenData]           = useState(null);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [scenario, setScenario]             = useState(null); // 'AB' | 'CD'
  const [step, setStep]                     = useState(1);    // 1–4 (coach-modus: 1–3)
  const [terminalState, setTerminalState]   = useState('');   // 'bedankt' | 'afgewezen' | 'verstuurd'

  // ── Coach-modus state ──
  const [coachClient, setCoachClient]       = useState(null); // { id, full_name, email }
  const [sendingDraft, setSendingDraft]     = useState(false);
  const [draftSendError, setDraftSendError] = useState('');
  // Onderdeel 3: types die de coach voor déze cliënt heeft uitgesloten
  const [excludedTypeIds, setExcludedTypeIds] = useState([]);
  // Onderdeel 5: bevestigingsmodal vóór versturen naar cliënt
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  // Custom regels (los eenmalig bedrag en/of credit-toekenning, coach-only)
  const [customLineItems, setCustomLineItems] = useState([]);
  const [customItemModalOpen, setCustomItemModalOpen] = useState(false);
  const [customItemDraft, setCustomItemDraft] = useState({ name: '', amount: '', creditAmount: '', creditPool: '' });
  const [customItemError, setCustomItemError] = useState('');
  // Sessietypes van bestaande actieve abonnementen van de cliënt (coach-modus) —
  // samen met chosenTypeIds bepaalt dit welke creditpools de coach mag kiezen
  // bij een custom credit-regel (zie migratie 20260703100000).
  const [existingActiveSessionTypes, setExistingActiveSessionTypes] = useState([]);
  // Onderdeel 1+2 (2026-07-28 vervolgsessie): membership_type_id → start_date
  // van abonnementen die déze cliënt al ACTIEF heeft — exact dezelfde
  // definitie als de guard in finalize_agreement_signing_multi
  // (client_memberships.status = 'active'). Gebruikt om in stap 1 (coach-
  // builder) al-actieve types uit te grijzen, en om vóór ondertekenen een
  // duidelijke waarschuwing te tonen i.p.v. pas na een mislukte submit.
  // Coach-modus: rechtstreeks uit client_memberships. Client-modus (token):
  // via get_agreement_by_token, want die pagina is anon/publiek.
  const [activeTypeStartDates, setActiveTypeStartDates] = useState({});
  // Onderdeel 2 (2026-07-11): openstaande tokens van deze client, puur
  // ter info — géén harde blokkade (create_agreement_token invalideert
  // deze al automatisch en atomisch bij versturen, zie migratie
  // 20260711100000).
  const [pendingTokensForClient, setPendingTokensForClient] = useState([]);

  // ── A/B state ──
  const [abType, setAbType]               = useState(null);
  const [abStaffelId, setAbStaffelId]     = useState('');
  const [abPayFreq, setAbPayFreq]         = useState('');
  const [abSessionDur, setAbSessionDur]   = useState(null);
  const [abErrors, setAbErrors]           = useState({});
  // Geselecteerde betaalde bonussen voor A/B (gratis bonussen altijd inbegrepen)
  const [abSelectedBonusIds, setAbSelectedBonusIds] = useState([]);

  // ── C/D state ──
  const [chosenTypeIds, setChosenTypeIds] = useState([]);
  // true wanneer de cliënt start met een door de coach voorgeselecteerde
  // draft_selection (stap 1 is dan niet beschikbaar/terugklikbaar)
  const [coachPrefilled, setCoachPrefilled] = useState(false);
  // typeConfig[typeId] = { staffelId, payFreq, selectedBonusIds: string[] }
  const [typeConfig, setTypeConfig]       = useState({});
  const [configIdx, setConfigIdx]         = useState(0);
  const [configSubStep, setConfigSubStep] = useState('staffel'); // 'staffel' | 'betaalfreq'
  const [cdErrors, setCdErrors]           = useState({});

  // ── Gezamenlijk ──
  const [agreementText, setAgreementText]       = useState('');
  const [signing, setSigning]                   = useState(false);
  const [hasSignature, setHasSignature]         = useState(false);
  const [isDrawing, setIsDrawing]               = useState(false);
  const [signError, setSignError]               = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  // F4-10: toegestane betaalmethoden (server-authoritatief uit de token-RPC)
  // en de door de client gekozen methode. 'manual' is altijd aanwezig.
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState(['manual']);
  const [paymentMethod, setPaymentMethod]       = useState('manual');
  // F4-10 Stap 4b: IBAN wordt uitsluitend gebruikt om mee te sturen naar
  // sign-agreement (→ Mollie mandaataanmaak) — nergens in eigen state/DB
  // voluit bewaard buiten dit formulier.
  const [iban, setIban]                         = useState('');
  // F4-10 SEPA-machtigingsaddendum: aparte akkoordcheckbox, los van de
  // algemene-voorwaarden-checkbox (agreementAccepted).
  const [sepaMandateAccepted, setSepaMandateAccepted] = useState(false);
  const [rejectModal, setRejectModal]           = useState(false);
  const [rejectReason, setRejectReason]         = useState('');
  const [rejecting, setRejecting]               = useState(false);
  // F4-10 SEPA-machtigingsaddendum: alleen voor de klant-preview onder het
  // IBAN-veld — de autoritatieve tekst (met echt Machtigingskenmerk) wordt
  // server-side in sign-agreement opgebouwd en aan agreement_text toegevoegd.
  const [sepaCompanySettings, setSepaCompanySettings] = useState({
    company_city: '', company_address: '', company_payment_term_days: '14', mollie_incassant_id: '',
  });
  // F4-11: NAW-gegevens — verplicht bij ondertekenen, voorgevuld indien al
  // bekend in het profiel (via get_agreement_by_token), corrigeerbaar.
  const [naamAdres, setNaamAdres] = useState({ street: '', house_number: '', postal_code: '', city: '' });

  useEffect(() => {
    if (coachMode) loadForCoach();
    else loadToken();
  }, [token, clientId]);
  useEffect(() => { if (step === 4 && canvasRef.current) setupCanvas(); }, [step]);
  useEffect(() => {
    supabase
      .from('settings')
      .select('key, value')
      .in('key', ['company_city', 'company_address', 'company_payment_term_days', 'mollie_incassant_id'])
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(({ key, value }) => { map[key] = value || ''; });
        setSepaCompanySettings(prev => ({ ...prev, ...map }));
      });
  }, []);

  // ── Coach-modus: client + membership types laden (geen token) ──
  async function loadForCoach() {
    setLoading(true);
    setError('');
    try {
      const { data: clientRow, error: clientErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, deleted_at')
        .eq('id', clientId)
        .maybeSingle();
      if (clientErr) throw clientErr;
      if (!clientRow) { setError(t('overeenkomst.clientNietGevonden')); setLoading(false); return; }
      if (clientRow.deleted_at) { setError(t('overeenkomst.clientVerwijderd')); setLoading(false); return; }
      setCoachClient(clientRow);

      // Bestaande actieve abonnementen: nodig om te bepalen welke
      // creditpools beschikbaar zijn voor een custom credit-regel
      // (zie migratie 20260703100000 — RPC koppelt aan een membership
      // binnen deze overeenkomst óf een bestaand actief membership).
      const { data: activeMemberships } = await supabase
        .from('client_memberships')
        .select('membership_type_id, session_type, start_date')
        .eq('client_id', clientId)
        .eq('status', 'active');
      setExistingActiveSessionTypes(
        [...new Set((activeMemberships || []).map(m => m.session_type).filter(Boolean))]
      );
      // Onderdeel 1: zelfde "al actief"-definitie als de backend-guard
      // (client_memberships.status = 'active'), t.b.v. uitgrijzen in stap 1.
      const activeDates = {};
      (activeMemberships || []).forEach(m => { activeDates[m.membership_type_id] = m.start_date; });
      setActiveTypeStartDates(activeDates);

      // Onderdeel 2: openstaande tokens van deze client tonen (info-banner,
      // zelfde definitie als ClientDetailPage's "pendingTokens").
      const { data: pendingData } = await supabase
        .from('agreement_tokens')
        .select('id, created_at, membership_type:membership_types(name)')
        .eq('client_id', clientId)
        .is('used_at', null)
        .is('rejected_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });
      setPendingTokensForClient(pendingData || []);

      const { data: typesData, error: typesErr } = await supabase
        .rpc('get_agreement_membership_types_for_coach');
      if (typesErr) throw typesErr;
      if (typesData?.error) { setError(t('overeenkomst.geenToegangCoachModus')); setLoading(false); return; }
      setMembershipTypes(typesData || []);
      setScenario('CD');
      setStep(1);
    } catch (err) {
      setError(t('overeenkomst.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Token laden ──────────────────────────────────────────────
  async function loadToken() {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcErr } = await supabase.rpc('get_agreement_by_token', { p_token: token });
      if (rpcErr) throw rpcErr;
      if (data?.error) {
        if (data.error === 'token_already_used')    setError(t('overeenkomst.tokenAlGebruikt'));
        else if (data.error === 'token_rejected')   setError(t('overeenkomst.tokenAfgewezenOfIngetrokken'));
        else                                        setError(t('overeenkomst.tokenOngeldig'));
        setLoading(false);
        return;
      }
      setTokenData(data);

      // F4-11: NAW-gegevens voorvullen indien al bekend in het profiel.
      setNaamAdres({
        street:       data.street || '',
        house_number: data.house_number || '',
        postal_code:  data.postal_code || '',
        city:         data.city || '',
      });

      // F4-10: toegestane betaalmethoden (systeem ∩ client, incl. 'manual').
      const methods = Array.isArray(data.allowed_payment_methods) && data.allowed_payment_methods.length > 0
        ? data.allowed_payment_methods
        : ['manual'];
      setAllowedPaymentMethods(methods);
      setPaymentMethod(methods.includes('manual') ? 'manual' : methods[0]);

      // Onderdeel 2 (2026-07-28 vervolgsessie): zelfde "al actief"-definitie
      // als de backend-guard, publiek meegegeven via de token-RPC (uitsluitend
      // membership_type_id + start_date van déze cliënt — geen andere gegevens).
      const activeDates = {};
      (data.already_active_memberships || []).forEach(m => { activeDates[m.membership_type_id] = m.start_date; });
      setActiveTypeStartDates(activeDates);

      const { data: typesData, error: typesErr } = await supabase
        .rpc('get_agreement_membership_types', { p_token: token });
      if (typesErr) throw typesErr;
      if (typesData?.error) {
        setError(t('overeenkomst.typeNietGevonden'));
        setLoading(false);
        return;
      }
      const types = typesData || [];
      setMembershipTypes(types);

      if (data.suggested_membership_type_id) {
        // Scenario A/B: coach heeft type voorgesteld
        const typeData = types.find(ty => ty.id === data.suggested_membership_type_id);
        if (!typeData) { setError(t('overeenkomst.typeNietGevonden')); setLoading(false); return; }
        setScenario('AB');
        setAbType(typeData);
        setStep(1);
      } else {
        // Scenario C/D: client kiest zelf (of vult de keuzes van de coach aan)
        setScenario('CD');
        const draft = data.draft_selection;
        if (Array.isArray(draft) && draft.length > 0) {
          const ids = draft.map(d => d.membership_type_id).filter(Boolean);
          const cfg = {};
          draft.forEach(d => {
            cfg[d.membership_type_id] = {
              staffelId:         d.membership_type_period_id || '',
              payFreq:           d.payment_frequency || '',
              selectedBonusIds:  d.selected_bonus_ids || [],
              isCustom:          !!d.is_custom,
              customName:        d.custom_name ?? '',
              customPrice:       d.custom_price ?? '',
              customCredits:     d.custom_credits ?? '',
            };
          });
          setChosenTypeIds(ids);
          setTypeConfig(cfg);
          setCoachPrefilled(true);

          const draftStart = resolveDraftStart(draft, types);
          if (draftStart) {
            setConfigIdx(draftStart.configIdx);
            setConfigSubStep(draftStart.configSubStep);
            setStep(2);
          } else {
            setStep(3);
          }
        } else {
          setStep(1);
        }
      }
    } catch (err) {
      setError(t('overeenkomst.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Canvas setup ─────────────────────────────────────────────
  function setupCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }

  function startDrawing(e) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
  }

  function draw(e) {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const ctx = canvas.getContext('2d');
    ctx.lineTo((clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY);
    ctx.stroke();
    setHasSignature(true);
  }

  function stopDrawing() { setIsDrawing(false); }

  function clearSignature() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  }

  // ── Ondertekenen ─────────────────────────────────────────────
  // F4-10 Stap 4b: basale IBAN-formaatvalidatie (landcode + lengte),
  // geen volledige MOD-97-checksum verplicht.
  function isValidIbanFormat(value) {
    const cleaned = (value || '').replace(/\s+/g, '').toUpperCase();
    return /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(cleaned);
  }

  // F4-11: NAW-gegevens verplicht bij ondertekenen (nodig voor de
  // "wonende te [woonplaats]"-verklaring in de overeenkomsttekst).
  function isValidPostalCode(value) {
    return /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/.test((value || '').trim());
  }
  function isNaamAdresValid() {
    return naamAdres.street.trim() !== ''
      && naamAdres.house_number.trim() !== ''
      && naamAdres.city.trim() !== ''
      && isValidPostalCode(naamAdres.postal_code);
  }

  async function handleSign() {
    if (!agreementAccepted) { setSignError(t('overeenkomst.akkoordVerplicht')); return; }
    if (!hasSignature)      { setSignError(t('overeenkomst.geenHandtekening')); return; }
    if (paymentMethod === 'sepa' && !isValidIbanFormat(iban)) {
      setSignError(t('overeenkomstBetaalmethode.ibanOngeldig'));
      return;
    }
    if (paymentMethod === 'sepa' && !sepaMandateAccepted) {
      setSignError(t('overeenkomstBetaalmethode.sepaMachtigingAkkoordVerplicht'));
      return;
    }
    if (!isNaamAdresValid()) {
      setSignError(t('overeenkomst.nawOngeldig'));
      return;
    }
    // Onderdeel 2 (2026-07-28 vervolgsessie): zelfde check als de disabled-
    // state van de ondertekenknop, hier herhaald als vangnet.
    if (getOverzichtItems().some(isHardConflict)) {
      setSignError(t('overeenkomst.alActiefWaarschuwingTitel'));
      return;
    }
    setSignError('');
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');

      let mainTypeId, mainStaffelId, mainPayFreq, additionalTypes;

      if (scenario === 'AB') {
        mainTypeId    = abType?.id;
        mainStaffelId = abStaffelId || null;
        const abStaffel = beschikbareStaffels(abType?.membership_type_periods).find(s => s.id === abStaffelId);
        const abStaffelFreq = abStaffel?.payment_frequency || 'period';
        mainPayFreq   = abStaffelFreq === 'both' ? (abPayFreq || 'period') : abStaffelFreq;
        additionalTypes = buildAdditionalTypesAB();
      } else {
        const mainId = chosenTypeIds[0];
        const mainCfg = typeConfig[mainId] || {};
        mainTypeId    = mainId;
        mainStaffelId = mainCfg.staffelId || null;
        mainPayFreq   = mainCfg.payFreq || 'period';
        additionalTypes = buildAdditionalTypesCD();
      }

      const { data, error: signErr } = await supabase.functions.invoke('sign-agreement', {
        body: {
          token,
          membership_type_id:          mainTypeId,
          staffel_id:                  mainStaffelId,
          payment_frequency:           mainPayFreq,
          payment_method:              paymentMethod,
          iban:                        paymentMethod === 'sepa' ? iban.replace(/\s+/g, '').toUpperCase() : undefined,
          sepa_mandate_accepted:       paymentMethod === 'sepa' ? sepaMandateAccepted : undefined,
          street:                      naamAdres.street.trim(),
          house_number:                naamAdres.house_number.trim(),
          postal_code:                 naamAdres.postal_code.trim(),
          city:                        naamAdres.city.trim(),
          signature_data:              signatureData,
          agreement_text:              agreementText,
          agreement_accepted:          true,
          ip_address:                  null,
          additional_membership_types: additionalTypes.length > 0 ? additionalTypes : undefined,
        },
      });

      if (signErr) {
        // Onderdeel 1 (2026-07-28 bugfix): bij een non-2xx zit de echte
        // foutmelding niet in signErr.message (dat is altijd de generieke
        // "Edge Function returned a non-2xx status code") maar in
        // signErr.context — zelfde uitleespatroon als elders in de app
        // (ClientDetailPage.jsx handleDeleteClient,
        // GebruikersBeheerPage.jsx). Zonder dit zag de cliënt/coach nooit
        // de duidelijke servermelding (bv. "Je hebt al een actief
        // abonnement van dit type"), maar altijd de onbegrijpelijke
        // Engelse generieke tekst.
        let body = null;
        try { body = await signErr.context?.json(); } catch { /* geen JSON-body */ }
        throw new Error(body?.message || body?.error || signErr.message);
      }
      if (data?.error) throw new Error(data.message || data.error);

      // Onderdeel 2 (2026-07-27): bij iDEAL is de factuur al direct
      // betaalbaar gemaakt (sign-agreement heeft server-side al een Mollie
      // Payment Link aangemaakt) — de client gaat direct door naar de
      // betaalpagina i.p.v. het bedankt-scherm te zien en te moeten
      // wachten tot de coach de factuur later handmatig verstuurt.
      if (data?.mollie_checkout_url) {
        window.location.href = data.mollie_checkout_url;
        return;
      }

      setTerminalState('bedankt');
    } catch (err) {
      setSignError(t('overeenkomst.foutOndertekenen') + err.message);
    } finally {
      setSigning(false);
    }
  }

  // ── Coach-modus: draft_selection opbouwen uit de huidige keuzes ──
  function buildDraftSelection() {
    return chosenTypeIds.map(tid => {
      const cfg = typeConfig[tid] || {};
      return {
        membership_type_id:        tid,
        membership_type_period_id: cfg.staffelId || null,
        payment_frequency:         cfg.payFreq || null,
        selected_bonus_ids:        cfg.selectedBonusIds || [],
        is_custom:                 !!cfg.isCustom,
        custom_name:               cfg.isCustom ? (cfg.customName || null) : null,
        custom_price:              cfg.isCustom && cfg.customPrice !== '' && cfg.customPrice != null ? Number(cfg.customPrice) : null,
        custom_credits:            cfg.isCustom && cfg.customCredits !== '' && cfg.customCredits != null ? Number(cfg.customCredits) : null,
      };
    });
  }

  // ── Custom regels: welke creditpools mag de coach kiezen? ──
  // De RPC (migratie 20260703100000) koppelt een custom credit-toekenning
  // aan een membership binnen déze overeenkomst óf een bestaand actief
  // membership van de cliënt — dus de UI beperkt het pool-dropdown tot
  // precies die sessietypes, zodat de coach nooit een combinatie kan
  // opslaan die de RPC bij ondertekenen zou weigeren.
  function allowedCreditPoolValues() {
    const chosenSessionTypes = chosenTypeIds
      .map(tid => membershipTypes.find(mt => mt.id === tid)?.session_type)
      .filter(Boolean);
    const allowedSessionTypes = new Set([...chosenSessionTypes, ...existingActiveSessionTypes]);
    return CUSTOM_CREDIT_POOLS.filter(p => allowedSessionTypes.has(p.sessionType)).map(p => p.value);
  }

  function openCustomItemModal() {
    setCustomItemDraft({ name: '', amount: '', creditAmount: '', creditPool: '' });
    setCustomItemError('');
    setCustomItemModalOpen(true);
  }

  function saveCustomItem() {
    const name = customItemDraft.name.trim();
    const hasAmount  = customItemDraft.amount !== '' && customItemDraft.amount != null;
    const hasCredits = customItemDraft.creditAmount !== '' && customItemDraft.creditAmount != null;

    if (!name) { setCustomItemError(t('overeenkomst.customRegelNaamVerplicht')); return; }
    if (!hasAmount && !hasCredits) { setCustomItemError(t('overeenkomst.customRegelMinimaalEen')); return; }
    if (hasCredits && !customItemDraft.creditPool) { setCustomItemError(t('overeenkomst.customRegelPoolVerplicht')); return; }
    if (hasCredits && Number(customItemDraft.creditAmount) <= 0) { setCustomItemError(t('overeenkomst.customRegelCreditsPositief')); return; }

    setCustomLineItems(prev => [...prev, {
      id: crypto.randomUUID(),
      name,
      amount: hasAmount ? Number(customItemDraft.amount) : null,
      credit_amount: hasCredits ? Number(customItemDraft.creditAmount) : null,
      credit_pool: hasCredits ? customItemDraft.creditPool : null,
    }]);
    setCustomItemModalOpen(false);
  }

  function removeCustomItem(id) {
    setCustomLineItems(prev => prev.filter(ci => ci.id !== id));
  }

  function customPoolLabel(value) {
    return CUSTOM_CREDIT_POOLS.find(p => p.value === value)?.label || value;
  }

  // Coach-modus: eigen state. Client-modus: uitsluitend uit tokenData
  // (DB-autoritatief) — de cliënt kan deze regels niet zelf zetten.
  function getCustomLineItemsForDisplay() {
    return coachMode ? customLineItems : (tokenData?.custom_line_items || []);
  }

  // ── Coach-modus: verstuur de tot dusver gemaakte keuzes naar de cliënt ──
  async function handleSendToClient() {
    if (!coachClient) return;
    setDraftSendError('');
    setSendingDraft(true);
    try {
      await sendDraftAgreementToClient(
        coachClient.id,
        user.id,
        buildDraftSelection(),
        coachClient.email,
        coachClient.full_name,
        excludedTypeIds,
        customLineItems
      );
      setSendConfirmOpen(false);
      setTerminalState('verstuurd');
    } catch (err) {
      setDraftSendError(t('overeenkomst.foutVersturen') + err.message);
    } finally {
      setSendingDraft(false);
    }
  }

  // ── Coach-modus: samenvatting voor de bevestigingsmodal (onderdeel 5) ──
  function getSendSummary() {
    const items = chosenTypeIds.map(tid => {
      const type = membershipTypes.find(mt => mt.id === tid);
      const cfg = typeConfig[tid] || {};
      const staffels = beschikbareStaffels(type?.membership_type_periods);
      const staffel = staffels.find(s => s.id === cfg.staffelId);
      const openParts = [];
      if (!staffel) openParts.push(t('overeenkomst.configuratieStaffelTitel'));
      else if (staffel.payment_frequency === 'both' && !cfg.payFreq) openParts.push(t('overeenkomstBetaalfreq.kiesTitel'));
      const naam = (cfg.isCustom && cfg.customName) ? cfg.customName : (type?.name || '');
      return { naam, open: openParts.length > 0 };
    });
    return { items, alleCompleet: items.length > 0 && items.every(i => !i.open) };
  }

  // ── Coach-modus: knop "Verstuur naar cliënt" (zichtbaar op elke stap) ──
  function SendToClientButton({ full = false }) {
    if (!coachMode) return null;
    return (
      <button
        onClick={() => setSendConfirmOpen(true)}
        disabled={sendingDraft}
        style={{
          width: full ? '100%' : undefined,
          flex: full ? undefined : 1, minWidth: full ? undefined : 200,
          background: sendingDraft ? '#93d3bd' : '#1a5c3a',
          color: 'white', border: 'none', borderRadius: 8,
          padding: '14px 24px', fontSize: 15, fontWeight: 700,
          cursor: sendingDraft ? 'not-allowed' : 'pointer',
        }}
      >
        {sendingDraft ? t('overeenkomst.verstuurBezig') : t('overeenkomst.verstuurNaarClient')}
      </button>
    );
  }

  // ── Bouw additional_membership_types voor A/B ─────────────────
  function buildAdditionalTypesAB() {
    const fromToken = (tokenData?.additional_membership_types || []);
    const hoofdMonthsAB = beschikbareStaffels(abType?.membership_type_periods)
      .find(s => s.id === abStaffelId)?.duration_months ?? null;
    const autoBonuses = (abType?.bonuses || [])
      .filter(b => {
        const { isFree } = bonusEffectief(b, hoofdMonthsAB);
        // Gratis bonussen altijd inbegrepen; betaalde bonussen alleen als aangevinkt
        return isFree || abSelectedBonusIds.includes(b.bonus_type_id);
      })
      .map(b => ({
        membership_type_id:        b.bonus_type_id,
        membership_type_period_id: null, // sign-agreement kiest de juiste staffel op basis van hoofdabonnement-looptijd
        bonus_price_override:      b.bonus_price_override,
        is_custom:                 false,
      }));
    // Merge: token items gaan voor (uniek op membership_type_id)
    const merged = [...fromToken];
    for (const auto of autoBonuses) {
      if (!merged.find(m => m.membership_type_id === auto.membership_type_id)) {
        merged.push(auto);
      }
    }
    return merged;
  }

  // ── Bouw additional_membership_types voor C/D ─────────────────
  function buildAdditionalTypesCD() {
    const additional = [];
    // Backend matcht bonus-staffels altijd op de looptijd van het
    // HOOFDabonnement (eerste gekozen type), ook voor bonussen van extra typen.
    const mainCfgCD = typeConfig[chosenTypeIds[0]] || {};
    const hoofdMonthsCD = beschikbareStaffels(
      membershipTypes.find(mt => mt.id === chosenTypeIds[0])?.membership_type_periods
    ).find(s => s.id === mainCfgCD.staffelId)?.duration_months ?? null;

    function addBonusesForType(typeId) {
      const type = membershipTypes.find(mt => mt.id === typeId);
      const selectedBonusIds = typeConfig[typeId]?.selectedBonusIds || [];
      for (const b of (type?.bonuses || [])) {
        const { isFree } = bonusEffectief(b, hoofdMonthsCD);
        // Gratis bonussen altijd; betaalde alleen als aangevinkt
        if (!isFree && !selectedBonusIds.includes(b.bonus_type_id)) continue;
        if (!additional.find(a => a.membership_type_id === b.bonus_type_id)) {
          additional.push({
            membership_type_id:        b.bonus_type_id,
            membership_type_period_id: null, // sign-agreement kiest de juiste staffel op basis van hoofdabonnement-looptijd
            bonus_price_override:      b.bonus_price_override,
            is_custom:                 false,
          });
        }
      }
    }

    // Overige gekozen typen (niet het eerste = main)
    for (let i = 1; i < chosenTypeIds.length; i++) {
      const tid = chosenTypeIds[i];
      const cfg = typeConfig[tid] || {};
      additional.push({
        membership_type_id:        tid,
        membership_type_period_id: cfg.staffelId || null,
        bonus_price_override:      null,
        // is_custom/custom_* worden server-side (sign-agreement) uitsluitend
        // uit draft_selection gelezen (DB-autoritatief), nooit uit deze
        // request-payload — zie O2-beveiligingsprincipe in BRIEFING.md.
        // Hier alsnog meegestuurd voor leesbaarheid/logging; de EF negeert
        // deze velden voor de daadwerkelijke prijs/naam/credits-berekening.
        is_custom:                 !!cfg.isCustom,
        payment_frequency:         cfg.payFreq || 'period',
      });
      addBonusesForType(tid);
    }
    // Auto-bonussen van het main type
    addBonusesForType(chosenTypeIds[0]);
    return additional;
  }

  // ── Afwijzen ─────────────────────────────────────────────────
  async function handleReject() {
    if (!rejectReason.trim()) {
      return;
    }
    setRejecting(true);
    try {
      const supabaseUrl = supabase.supabaseUrl;
      const res = await fetch(`${supabaseUrl}/functions/v1/reject-agreement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: rejectReason }),
      });
      const result = await res.json();
      if (!res.ok || result?.error) throw new Error(result?.error || 'Onbekende fout');
      setRejectModal(false);
      setTerminalState('afgewezen');
    } catch (err) {
      setError(t('overeenkomst.foutAfwijzen') + ': ' + err.message);
    } finally {
      setRejecting(false);
    }
  }

  // ── Agreement text opstellen ──────────────────────────────────
  function buildAgreementText() {
    const parts = [];

    // Centrale algemene voorwaarden (1×, bovenaan)
    const generalTerms = (tokenData?.general_terms || '').trim();
    if (generalTerms) {
      parts.push(generalTerms);
    }

    if (scenario === 'AB') {
      const staffel = beschikbareStaffels(abType?.membership_type_periods)
        .find(s => s.id === abStaffelId);
      const typeText = (abType?.agreement_text || '').trim();
      if (typeText) {
        if (parts.length > 0) parts.push('\n\n' + t('overeenkomst.voorwaardenTypeKop') + ': ' + (abType?.name || ''));
        parts.push(replaceVariables(typeText, abType, tokenData, staffel));
      }
    } else {
      for (const tid of chosenTypeIds) {
        const type = membershipTypes.find(mt => mt.id === tid);
        const cfg = typeConfig[tid] || {};
        const staffel = beschikbareStaffels(type?.membership_type_periods)
          .find(s => s.id === cfg.staffelId);
        const typeText = (type?.agreement_text || '').trim();
        if (typeText) {
          if (parts.length > 0) parts.push('\n\n' + t('overeenkomst.voorwaardenTypeKop') + ': ' + (type?.name || ''));
          parts.push(replaceVariables(typeText, type, tokenData, staffel));
        }
      }
    }

    return parts.join('\n\n') || t('overeenkomst.geenTekst');
  }

  // ── Overzicht items samenstellen ─────────────────────────────
  function getOverzichtItems() {
    const items = [];
    if (scenario === 'AB') {
      const staffels = beschikbareStaffels(abType?.membership_type_periods);
      const staffel = staffels.find(s => s.id === abStaffelId) || staffels[0];
      const isCustom = tokenData?.is_custom;
      const pricePerMonth = isCustom && tokenData?.custom_price != null
        ? Number(tokenData.custom_price)
        : staffel?.price_per_month;
      // Onderdeel 2 (2026-07-28 bugfix): credits horen bij dezelfde
      // isCustom-override als naam/prijs — zie CD-tak hieronder voor de
      // volledige toelichting.
      const credits = isCustom && tokenData?.custom_credits != null
        ? Number(tokenData.custom_credits)
        : (abType?.credits_per_period ?? null);
      items.push({
        naam: (isCustom && tokenData?.custom_name) ? tokenData.custom_name : abType?.name,
        pricePerMonth,
        credits,
        months: staffel?.duration_months,
        paymentFreq: staffel?.payment_frequency === 'both' ? (abPayFreq || 'period') : (staffel?.payment_frequency || 'period'),
        isBonus: false,
        isFree: false,
        sessionType: abType?.session_type,
        promoLabel: staffel?.promo_label || null,
        membershipTypeId: abType?.id,
      });
      // Bonussen (alleen geselecteerde) — O1: prijs én looptijd van de op
      // looptijd gematchte staffel, identiek aan wat de backend factureert.
      const mainPayFreqAB = staffel?.payment_frequency === 'both' ? (abPayFreq || 'period') : (staffel?.payment_frequency || 'period');
      const hoofdMndAB = staffel?.duration_months ?? null;
      for (const b of (abType?.bonuses || [])) {
        const { isFree, pricePerMonth: bpm, months: bonusMnd } = bonusEffectief(b, hoofdMndAB);
        if (!isFree && !abSelectedBonusIds.includes(b.bonus_type_id)) continue;
        items.push({
          naam: b.bonus_type_name,
          pricePerMonth: isFree ? null : bpm,
          months: bonusMnd ?? hoofdMndAB,
          paymentFreq: mainPayFreqAB,
          isBonus: true,
          isFree,
          sessionType: b.bonus_session_type,
          promoLabel: null,
          staffelAfwijking: (bonusMnd != null && hoofdMndAB != null && bonusMnd !== hoofdMndAB)
            ? t('overeenkomst.bonusLooptijdAfwijking', { hoofd: hoofdMndAB, bonus: bonusMnd })
            : null,
          membershipTypeId: b.bonus_type_id,
        });
      }
    } else {
      for (let i = 0; i < chosenTypeIds.length; i++) {
        const tid = chosenTypeIds[i];
        const type = membershipTypes.find(mt => mt.id === tid);
        const cfg = typeConfig[tid] || {};
        const staffel = beschikbareStaffels(type?.membership_type_periods)
          .find(s => s.id === cfg.staffelId);
        const typePayFreq = cfg.payFreq || staffel?.payment_frequency || 'period';
        // Bug 2 (2026-07-28): "Aangepaste voorwaarden voor dit abonnement"
        // (cfg.isCustom/customName/customPrice/customCredits, ingevuld op
        // de Configuratie-stap) werd hier genegeerd — de Overzicht-stap én
        // de client-facing ondertekenpagina (beide gebruiken deze functie,
        // zie renderStep3/renderStep4) toonden altijd de standaard
        // naam/prijs van het abonnementstype, ook als de coach een custom
        // naam/prijs/credits had ingevuld. sign-agreement/finalize_agreement_
        // signing_multi gebruiken de custom-override wél al correct — dit
        // was dus zuiver een frontend-weergaveprobleem.
        const typeIsCustom = !!cfg.isCustom;
        items.push({
          naam: (typeIsCustom && cfg.customName) ? cfg.customName : type?.name,
          pricePerMonth: (typeIsCustom && cfg.customPrice !== '' && cfg.customPrice != null)
            ? Number(cfg.customPrice)
            : staffel?.price_per_month,
          credits: (typeIsCustom && cfg.customCredits !== '' && cfg.customCredits != null)
            ? Number(cfg.customCredits)
            : (type?.credits_per_period ?? null),
          months: staffel?.duration_months,
          paymentFreq: typePayFreq,
          isBonus: false,
          isFree: false,
          sessionType: type?.session_type,
          promoLabel: staffel?.promo_label || null,
          membershipTypeId: type?.id,
        });
        // Bonussen: betaalfrequentie van het hoofdtype (index 0); O1: prijs
        // én looptijd van de op de HOOFD-looptijd gematchte staffel — de
        // backend matcht álle bonussen op de looptijd van het hoofdabonnement.
        const mainTypeId = chosenTypeIds[0];
        const mainCfg = typeConfig[mainTypeId] || {};
        const mainStaffel = beschikbareStaffels(
          membershipTypes.find(mt => mt.id === mainTypeId)?.membership_type_periods
        ).find(s => s.id === mainCfg.staffelId);
        const mainPayFreq = mainCfg.payFreq || mainStaffel?.payment_frequency || 'period';
        const hoofdMndCD = mainStaffel?.duration_months ?? null;
        const selectedBonusIds = typeConfig[tid]?.selectedBonusIds || [];
        for (const b of (type?.bonuses || [])) {
          const { isFree, pricePerMonth: bpm, months: bonusMnd } = bonusEffectief(b, hoofdMndCD);
          if (!isFree && !selectedBonusIds.includes(b.bonus_type_id)) continue;
          items.push({
            naam: b.bonus_type_name,
            pricePerMonth: isFree ? null : bpm,
            months: bonusMnd ?? hoofdMndCD,
            paymentFreq: mainPayFreq,
            isBonus: true,
            isFree,
            sessionType: b.bonus_session_type,
            promoLabel: null,
            staffelAfwijking: (bonusMnd != null && hoofdMndCD != null && bonusMnd !== hoofdMndCD)
              ? t('overeenkomst.bonusLooptijdAfwijking', { hoofd: hoofdMndCD, bonus: bonusMnd })
              : null,
            membershipTypeId: b.bonus_type_id,
          });
        }
      }
    }
    return items;
  }

  // Onderdeel 2 (2026-07-28 vervolgsessie): een item is een HARDE botsing
  // (blokkeert ondertekenen) als het een expliciet gekozen type is — het
  // hoofdtype, of een expliciet aangevinkte betaalde bonus — en de cliënt
  // dat type al actief heeft. Een gratis auto-bonus die de cliënt al actief
  // heeft is GEEN harde botsing: sign-agreement slaat die stil over (Bug 1-
  // fix), dus alleen een informatief label, geen blokkade.
  function isHardConflict(item) {
    if (!item.membershipTypeId) return false;
    if (activeTypeStartDates[item.membershipTypeId] == null) return false;
    return !item.isBonus || !item.isFree;
  }
  function alActiefSindsLabel(typeId) {
    const startDate = activeTypeStartDates[typeId];
    if (!startDate) return null;
    return t('overeenkomst.alActiefSinds', { datum: new Date(startDate).toLocaleDateString('nl-NL') });
  }

  // ── Stepper labels ───────────────────────────────────────────
  const stepperLabels = coachMode
    ? t('overeenkomst.stepperCDStepsCoach').split(',')
    : scenario === 'AB'
      ? t('overeenkomst.stepperABSteps').split(',')
      : t('overeenkomst.stepperCDSteps').split(',');

  // ─────────────────────────────────────────────────────────────
  // RENDER: laad- en fout-states
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageShell>
        <div style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #1d9e75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#666', fontSize: 14 }}>{t('overeenkomst.laden')}</p>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell maxWidth={500}>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, marginBottom: 12 }}>{t('overeenkomst.foutTitel')}</h2>
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>{error}</p>
        </div>
      </PageShell>
    );
  }

  if (terminalState === 'bedankt') {
    return (
      <PageShell maxWidth={500}>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>✅</div>
          <h2 style={{ fontWeight: 700, fontSize: 24, color: '#1d9e75', marginBottom: 16 }}>{t('overeenkomst.bedanktTitel')}</h2>
          <p style={{ color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>{t('overeenkomst.bedanktTekst')}</p>
          <p style={{ color: '#999', fontSize: 13 }}>{t('overeenkomst.bedanktSluiten')}</p>
        </div>
      </PageShell>
    );
  }

  if (terminalState === 'afgewezen') {
    return (
      <PageShell maxWidth={500}>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>↩️</div>
          <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 16 }}>{t('overeenkomst.afgewezenTitel')}</h2>
          <p style={{ color: '#555', fontSize: 14, lineHeight: 1.6 }}>{t('overeenkomst.afgewezenTekst')}</p>
        </div>
      </PageShell>
    );
  }

  if (terminalState === 'verstuurd') {
    return (
      <PageShell maxWidth={500}>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>✅</div>
          <h2 style={{ fontWeight: 700, fontSize: 24, color: '#1d9e75', marginBottom: 16 }}>{t('overeenkomst.verstuurGeluktTitel')}</h2>
          <p style={{ color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>{t('overeenkomst.verstuurGeluktTekst')}</p>
          <button
            onClick={() => navigate(`/coach/clients/${clientId}`)}
            style={{
              background: '#1d9e75', color: 'white', border: 'none', borderRadius: 8,
              padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t('overeenkomst.stepTerug')}
          </button>
        </div>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // RENDER: hoofd-flow
  // ─────────────────────────────────────────────────────────────
  return (
    <PageShell maxWidth={step <= 2 ? 860 : 720} title={coachMode ? t('overeenkomst.coachModusTitel') : undefined}>
      {/* Client naam onder header */}
      <div style={{ background: '#f9fafb', padding: '8px 28px', fontSize: 13, color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
        {coachMode ? t('overeenkomst.coachModusUitleg', { naam: coachClient?.full_name || '' }) : tokenData?.client_name}
      </div>

      {/* Onderdeel 2: info-banner bij al openstaande token(s) voor deze client */}
      {coachMode && pendingTokensForClient.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, margin: '16px 28px 0', padding: '12px 16px' }}>
          <p style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 6 }}>
            {t('overeenkomst.openstaandTokenBannerTitel')}
          </p>
          {pendingTokensForClient.map(tk => (
            <p key={tk.id} style={{ fontSize: 13, color: '#92400e', marginBottom: 2 }}>
              {t('overeenkomst.openstaandTokenBannerRegel', {
                datum: fmtDate(tk.created_at),
                type: tk.membership_type?.name || t('overeenkomst.openstaandTokenBannerType'),
              })}
            </p>
          ))}
          <p style={{ fontSize: 12, color: '#92400e', marginTop: 6, fontStyle: 'italic' }}>
            {t('overeenkomst.openstaandTokenBannerUitleg')}
          </p>
        </div>
      )}

      {/* Stepper */}
      <Stepper labels={stepperLabels} current={step} />

      <div style={{ padding: '24px 28px 32px' }}>
        {/* ── SCENARIO A/B ── */}
        {scenario === 'AB' && step === 1 && renderABStep1()}
        {scenario === 'AB' && step === 2 && renderABStep2()}
        {scenario === 'AB' && step === 3 && renderStep3()}
        {scenario === 'AB' && step === 4 && renderStep4()}

        {/* ── SCENARIO C/D ── */}
        {scenario === 'CD' && step === 1 && renderCDStep1()}
        {scenario === 'CD' && step === 2 && renderCDStep2()}
        {scenario === 'CD' && step === 3 && renderStep3()}
        {scenario === 'CD' && step === 4 && renderStep4()}
      </div>

      {/* Custom regel toevoegen modal (Onderdeel 1) — sluit nooit op backdrop-klik */}
      {customItemModalOpen && (() => {
        const allowedPools = allowedCreditPoolValues();
        const hasCredits = customItemDraft.creditAmount !== '' && customItemDraft.creditAmount != null;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 32, maxWidth: 440, width: '100%' }}
              onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{t('overeenkomst.customRegelModalTitel')}</h2>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 18, lineHeight: 1.6 }}>{t('overeenkomst.customRegelModalUitleg')}</p>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('overeenkomst.customRegelNaamLabel')}</label>
              <input
                type="text"
                value={customItemDraft.name}
                onChange={e => setCustomItemDraft(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('overeenkomst.customRegelNaamPlaceholder')}
                style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('overeenkomst.customRegelBedragLabel')}</label>
              <input
                type="number"
                step="0.01"
                value={customItemDraft.amount}
                onChange={e => setCustomItemDraft(prev => ({ ...prev, amount: e.target.value }))}
                placeholder={t('overeenkomst.customRegelBedragPlaceholder')}
                style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: 14, marginBottom: 4, boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>{t('overeenkomst.customRegelBedragHelper')}</p>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('overeenkomst.customRegelCreditsLabel')}</label>
              <input
                type="number"
                min="1"
                step="1"
                value={customItemDraft.creditAmount}
                onChange={e => setCustomItemDraft(prev => ({ ...prev, creditAmount: e.target.value }))}
                placeholder={t('overeenkomst.customRegelCreditsPlaceholder')}
                style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
              />

              {hasCredits && (
                <>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{t('overeenkomst.customRegelPoolLabel')}</label>
                  {allowedPools.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 14 }}>{t('overeenkomst.customRegelGeenPools')}</p>
                  ) : (
                    <select
                      value={customItemDraft.creditPool}
                      onChange={e => setCustomItemDraft(prev => ({ ...prev, creditPool: e.target.value }))}
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', padding: '10px 12px', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
                    >
                      <option value="">{t('overeenkomst.customRegelPoolKies')}</option>
                      {CUSTOM_CREDIT_POOLS.filter(p => allowedPools.includes(p.value)).map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  )}
                </>
              )}

              {customItemError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{customItemError}</p>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={() => setCustomItemModalOpen(false)}
                  style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                  {t('overeenkomst.afwijzenAnnuleer')}
                </button>
                <button onClick={saveCustomItem}
                  style={{ background: '#1d9e75', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', fontWeight: 700 }}>
                  {t('overeenkomst.customRegelOpslaan')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Afwijzen modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 32, maxWidth: 480, width: '100%' }}
            onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>{t('overeenkomst.afwijzenTitel')}</h2>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 12, lineHeight: 1.6 }}>{t('overeenkomst.afwijzenUitleg')}</p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder={t('overeenkomst.afwijzenRedenPlaceholder')}
              style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', padding: 12, fontSize: 14, minHeight: 90, resize: 'vertical', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectModal(false)} disabled={rejecting}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                {t('overeenkomst.afwijzenAnnuleer')}
              </button>
              <button onClick={handleReject} disabled={rejecting || !rejectReason.trim()}
                style={{ background: rejecting || !rejectReason.trim() ? '#fca5a5' : '#dc2626', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: rejecting || !rejectReason.trim() ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                {rejecting ? t('overeenkomst.afwijzenBezig') : t('overeenkomst.afwijzenBevestig')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bevestig-versturen modal (onderdeel 5) — sluit nooit op backdrop-klik */}
      {sendConfirmOpen && (() => {
        const summary = getSendSummary();
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
            <div style={{ background: 'white', borderRadius: 12, padding: 32, maxWidth: 480, width: '100%' }}
              onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{t('overeenkomst.verstuurBevestigTitel')}</h2>

              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {t('overeenkomst.verstuurBevestigOntvanger')}
              </div>
              <div style={{ fontSize: 14, color: '#1a1a2e', marginBottom: 16 }}>
                {coachClient?.full_name} — {coachClient?.email}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {t('overeenkomst.verstuurBevestigAbonnementen')}
              </div>
              <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 14, color: '#1a1a2e' }}>
                {summary.items.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: 4 }}>
                    {item.naam}
                    {item.open && <span style={{ color: '#b45309', fontSize: 12, marginLeft: 6 }}>({t('overeenkomst.verstuurBevestigOpenstaand')})</span>}
                  </li>
                ))}
              </ul>

              <p style={{ fontSize: 13, color: summary.alleCompleet ? '#15803d' : '#6b7280', marginBottom: 20, lineHeight: 1.5 }}>
                {summary.alleCompleet ? t('overeenkomst.verstuurBevestigGeenOpenstaand') : t('overeenkomst.verstuurBevestigOpenstaand')}
              </p>

              {draftSendError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{draftSendError}</p>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setSendConfirmOpen(false)} disabled={sendingDraft}
                  style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer', fontWeight: 600 }}>
                  {t('overeenkomst.verstuurBevestigAnnuleren')}
                </button>
                <button onClick={handleSendToClient} disabled={sendingDraft}
                  style={{ background: sendingDraft ? '#93d3bd' : '#1a5c3a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: sendingDraft ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                  {sendingDraft ? t('overeenkomst.verstuurBezig') : t('overeenkomst.verstuurBevestigBevestigen')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </PageShell>
  );

  // ─────────────────────────────────────────────────────────────
  // A/B STAP 1 — Staffel kiezen
  // ─────────────────────────────────────────────────────────────
  function renderABStep1() {
    const staffels = beschikbareStaffels(abType?.membership_type_periods);
    const cfg = SESSION_CONFIG[abType?.session_type] || {};

    return (
      <>
        {/* Type-header */}
        <div style={{
          background: cfg.bg || '#f9fafb',
          border: `1px solid ${cfg.border || '#e5e7eb'}`,
          borderLeft: `4px solid ${cfg.color || '#1d9e75'}`,
          borderRadius: 8, padding: '14px 18px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ background: cfg.color || '#1d9e75', color: 'white', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 12 }}>
              {cfg.badge}
            </span>
            <strong style={{ fontSize: 16 }}>{abType?.name}</strong>
            {abType?.description && (
              <span style={{ fontSize: 13, color: '#6b7280' }}>— {abType.description}</span>
            )}
          </div>
        </div>

        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{t('overeenkomst.scenarioABStaffelTitel')}</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>{t('overeenkomst.scenarioABOmschrijving')}</p>

        {staffels.length === 0 ? (
          <div style={{ padding: 24, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, textAlign: 'center', color: '#92400e', fontSize: 14 }}>
            {t('overeenkomst.typeNietGevonden')}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
            {staffels.map(s => (
              <StaffelKaart
                key={s.id}
                staffel={s}
                selected={abStaffelId === s.id}
                onClick={() => { setAbStaffelId(s.id); setAbPayFreq(''); setAbErrors({}); }}
              />
            ))}
          </div>
        )}

        {/* Sessieduur (pt_plus) */}
        {abType?.session_type === 'pt_plus' && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>{t('overeenkomst.sessieduurKiezen')}</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {[30, 60].map(min => (
                <div
                  key={min}
                  onClick={() => { setAbSessionDur(min); setAbErrors(e => ({ ...e, sessionDur: '' })); }}
                  style={{
                    border: `2px solid ${abSessionDur === min ? '#7e22ce' : '#e5e7eb'}`,
                    borderRadius: 8, padding: '12px 24px', cursor: 'pointer',
                    background: abSessionDur === min ? '#faf5ff' : 'white',
                    fontWeight: abSessionDur === min ? 700 : 400, fontSize: 14,
                  }}
                >
                  {min === 30 ? t('overeenkomst.sessieduur30') : t('overeenkomst.sessieduur60')}
                </div>
              ))}
            </div>
            {abErrors.sessionDur && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{abErrors.sessionDur}</p>}
          </div>
        )}

        {/* Bonussen A/B (2a) */}
        {(abType?.bonuses || []).length > 0 && (
          <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#374151' }}>{t('overeenkomst.bonussenSectieLabel')}</div>
            {(abType.bonuses).map((b, idx) => {
              const { isFree, pricePerMonth: bpm } = bonusEffectief(b,
                staffels.find(s => s.id === abStaffelId)?.duration_months ?? null);
              if (isFree) {
                return (
                  <div key={idx} style={{ fontSize: 13, color: '#15803d', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>🎁</span>
                    <span>{t('overeenkomst.bonusInclusiefLabel')}: <strong>{b.bonus_type_name}</strong> — {t('overeenkomst.bonusGratis')}</span>
                  </div>
                );
              }
              const isSelected = abSelectedBonusIds.includes(b.bonus_type_id);
              return (
                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={e => {
                      setAbSelectedBonusIds(prev =>
                        e.target.checked
                          ? [...prev, b.bonus_type_id]
                          : prev.filter(id => id !== b.bonus_type_id)
                      );
                    }}
                    style={{ width: 15, height: 15, accentColor: '#1d9e75', cursor: 'pointer' }}
                  />
                  <span>{t('overeenkomst.bonusVoegToe')}: <strong>{b.bonus_type_name}</strong> — {euro(bpm)}/mnd</span>
                </label>
              );
            })}
          </div>
        )}

        {abErrors.staffel && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{abErrors.staffel}</p>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleABStep1Next}
            disabled={!abStaffelId}
            style={{
              flex: 1, minWidth: 180, background: abStaffelId ? '#1d9e75' : '#d1d5db',
              color: 'white', border: 'none', borderRadius: 8, padding: '14px 24px',
              fontSize: 15, fontWeight: 700, cursor: abStaffelId ? 'pointer' : 'not-allowed',
            }}
          >
            {t('overeenkomst.stepVolgende')} →
          </button>
          <button onClick={() => setRejectModal(true)}
            style={{ background: 'transparent', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '14px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {t('overeenkomst.afwijzenKnop')}
          </button>
        </div>
      </>
    );
  }

  function handleABStep1Next() {
    const errors = {};
    if (!abStaffelId) { errors.staffel = t('overeenkomst.staffelVerplicht'); }
    if (abType?.session_type === 'pt_plus' && abType?.session_duration_minutes == null && abSessionDur == null) {
      errors.sessionDur = t('overeenkomst.sessieduurVerplicht');
    }
    if (Object.keys(errors).length > 0) { setAbErrors(errors); return; }

    const staffel = beschikbareStaffels(abType?.membership_type_periods).find(s => s.id === abStaffelId);
    if (staffel?.payment_frequency === 'both') {
      setStep(2);
    } else {
      setAbPayFreq(staffel?.payment_frequency || 'period');
      const text = buildAgreementText();
      setAgreementText(text);
      setStep(3);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // A/B STAP 2 — Betaalfrequentie
  // ─────────────────────────────────────────────────────────────
  function renderABStep2() {
    const staffels = beschikbareStaffels(abType?.membership_type_periods);
    const staffel  = staffels.find(s => s.id === abStaffelId);

    return (
      <>
        <BackBtn onClick={() => { setAbPayFreq(''); setStep(1); }} />
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>{t('overeenkomstBetaalfreq.kiesTitel')}</h2>

        <BetaalfreqKeuze
          staffel={staffel}
          selectedFreq={abPayFreq}
          onSelect={freq => { setAbPayFreq(freq); setAbErrors({}); }}
        />

        {abErrors.payFreq && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{abErrors.payFreq}</p>}

        <button
          onClick={() => {
            if (!abPayFreq) { setAbErrors({ payFreq: t('overeenkomstBetaalfreq.verplicht') }); return; }
            setAgreementText(buildAgreementText());
            setStep(3);
          }}
          disabled={!abPayFreq}
          style={{
            width: '100%', marginTop: 20,
            background: abPayFreq ? '#1d9e75' : '#d1d5db',
            color: 'white', border: 'none', borderRadius: 8, padding: '14px 24px',
            fontSize: 15, fontWeight: 700, cursor: abPayFreq ? 'pointer' : 'not-allowed',
          }}
        >
          {t('overeenkomst.stepVolgende')} →
        </button>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // C/D STAP 1 — Type kiezen per sessietype
  // ─────────────────────────────────────────────────────────────
  function renderCDStep1() {
    return (
      <>
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{t('overeenkomst.scenarioCDTitel')}</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 24, lineHeight: 1.6 }}>{t('overeenkomst.scenarioCDUitleg')}</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
          {SESSION_ORDER.map(sessionKey => {
            const cfg        = SESSION_CONFIG[sessionKey];
            const typesInSec = membershipTypes.filter(mt => mt.session_type === sessionKey && !excludedTypeIds.includes(mt.id));
            if (typesInSec.length === 0) return null;

            // Max één per sectie
            const sectionChosenId = chosenTypeIds.find(id =>
              membershipTypes.find(mt => mt.id === id && mt.session_type === sessionKey)
            );

            return (
              <div key={sessionKey}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                  padding: '8px 14px', background: cfg.bg,
                  border: `1px solid ${cfg.border}`, borderRadius: 6,
                }}>
                  <span style={{ background: cfg.color, color: 'white', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                    {cfg.badge}
                  </span>
                  <span style={{ fontWeight: 700, color: cfg.color, fontSize: 14 }}>{cfg.label}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                  {typesInSec.map(type => {
                    const isChosen    = chosenTypeIds.includes(type.id);
                    const staffels    = beschikbareStaffels(type.membership_type_periods);
                    const lowestPrice = staffels.reduce((min, s) => (s.price_per_month ?? Infinity) < min ? (s.price_per_month ?? Infinity) : min, Infinity);
                    const bonuses     = type.bonuses || [];
                    const promoLbl    = bestPromoLabel(type.membership_type_periods);
                    const selectedBonusIds = typeConfig[type.id]?.selectedBonusIds || [];
                    // Onderdeel 1 (2026-07-28 vervolgsessie): zelfde "al actief"-
                    // definitie als de backend-guard (client_memberships.status
                    // = 'active') — hier al uitgrijzen i.p.v. pas bij ondertekenen
                    // een foutmelding tonen.
                    const isAlreadyActive = activeTypeStartDates[type.id] != null;

                    return (
                      <div key={type.id}>
                        <div
                          className="oe-type-card"
                          onClick={() => {
                            if (isAlreadyActive) return;
                            if (isChosen) {
                              setChosenTypeIds(prev => prev.filter(id => id !== type.id));
                              setCdErrors({});
                            } else {
                              setChosenTypeIds(prev => {
                                const withoutSameSection = prev.filter(id => {
                                  const mt = membershipTypes.find(m => m.id === id);
                                  return mt?.session_type !== sessionKey;
                                });
                                return [...withoutSameSection, type.id];
                              });
                              setCdErrors({});
                            }
                          }}
                          style={{
                            border: `2px solid ${isChosen ? cfg.color : '#e5e7eb'}`,
                            borderRadius: 10,
                            padding: '16px 18px',
                            cursor: isAlreadyActive ? 'not-allowed' : 'pointer',
                            background: isAlreadyActive ? '#f3f4f6' : (isChosen ? cfg.bg : 'white'),
                            opacity: isAlreadyActive ? 0.6 : 1,
                            transition: 'all 0.15s',
                            position: 'relative',
                          }}
                        >
                          {/* Onderdeel 1: al actief bij deze cliënt — uitgegrijsd */}
                          {isAlreadyActive && (
                            <div style={{
                              position: 'absolute', top: 10, left: 10,
                              background: '#9ca3af', color: 'white',
                              borderRadius: 6, padding: '2px 10px',
                              fontSize: 11, fontWeight: 700,
                            }}>{alActiefSindsLabel(type.id)}</div>
                          )}
                          {/* Onderdeel 3: type uitsluiten voor deze cliënt (coach-modus) */}
                          {coachMode && !isAlreadyActive && (
                            <button
                              type="button"
                              className="oe-exclude-btn"
                              title={t('overeenkomst.uitsluitenKnopTitel')}
                              onClick={e => {
                                e.stopPropagation();
                                setExcludedTypeIds(prev => [...prev, type.id]);
                                setChosenTypeIds(prev => prev.filter(id => id !== type.id));
                              }}
                              style={{
                                position: 'absolute', top: 8, right: isChosen ? 36 : 8,
                                width: 20, height: 20, borderRadius: '50%',
                                background: '#f3f4f6', border: '1px solid #e5e7eb',
                                color: '#6b7280', fontSize: 12, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', lineHeight: 1, zIndex: 1,
                              }}
                            >
                              ×
                            </button>
                          )}
                          {/* Aanbevolen badge (2c) — niet tonen als het type al actief is */}
                          {type.is_recommended && !isAlreadyActive && (
                            <div style={{
                              position: 'absolute', top: 10, left: 10,
                              background: '#1d9e75', color: 'white',
                              borderRadius: 6, padding: '2px 10px',
                              fontSize: 11, fontWeight: 700,
                            }}>{t('overeenkomst.aanbevolenBadge')}</div>
                          )}
                          {isChosen && (
                            <div style={{
                              position: 'absolute', top: 10, right: 10,
                              background: cfg.color, color: 'white',
                              borderRadius: '50%', width: 22, height: 22,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 12, fontWeight: 700,
                            }}>✓</div>
                          )}
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: '#1a1a2e', paddingRight: isChosen ? 28 : 0, paddingTop: (type.is_recommended || isAlreadyActive) ? 22 : 0 }}>
                            {type.name}
                          </div>
                          {type.description && (
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 8, lineHeight: 1.5 }}>{type.description}</div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            <span style={{
                              background: cfg.bg, color: cfg.color,
                              border: `1px solid ${cfg.border}`,
                              borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                            }}>
                              {cfg.badge} {cfg.label}
                            </span>
                            {type.sessions_per_week_frequency > 0 && (
                              <span style={{ fontSize: 12, color: '#6b7280' }}>
                                {type.sessions_per_week_frequency}×/week
                              </span>
                            )}
                          </div>
                          {staffels.length > 0 && lowestPrice !== Infinity && (
                            <div style={{ fontSize: 13, color: '#1d9e75', fontWeight: 600 }}>
                              {t('overeenkomst.vanafPrijs')} {euro(lowestPrice)}/mnd
                            </div>
                          )}
                          {/* Promo-label (2b) */}
                          {promoLbl && (
                            <div style={{ fontSize: 11, color: '#15803d', marginTop: 4, fontWeight: 600 }}>
                              🎁 {promoLbl}
                            </div>
                          )}
                        </div>

                        {/* Gratis bonussen: altijd zichtbaar op kaart (ook vóór selectie).
                            Onderdeel 1: als de cliënt de bonus al actief heeft, wordt hij
                            bij ondertekenen sowieso overgeslagen (Bug 1-fix) — hier puur
                            informatief, geen blokkade. */}
                        {bonuses.some(b => bonusEffectief(b).isFree) && (
                          <div style={{ marginTop: 8, paddingLeft: 4 }}>
                            {bonuses.filter(b => bonusEffectief(b).isFree).map((b, idx) => {
                              const bonusAlreadyActive = activeTypeStartDates[b.bonus_type_id] != null;
                              return (
                                <div key={idx} style={{ fontSize: 12, color: bonusAlreadyActive ? '#6b7280' : '#15803d', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span>{bonusAlreadyActive ? '✓' : '🎁'}</span>
                                  <span>
                                    {t('overeenkomst.bonusInclusiefLabel')}: <strong>{b.bonus_type_name}</strong> —{' '}
                                    {bonusAlreadyActive ? alActiefSindsLabel(b.bonus_type_id) : t('overeenkomst.bonusGratis')}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Betaalde bonussen: checkboxes, alleen bij selectie. Onderdeel 1:
                            een al-actieve bonus wordt uitgeschakeld — expliciet aanvinken
                            zou een expliciete dubbele selectie zijn, die de backend-guard
                            wél hard blokkeert (i.t.t. de stilzwijgend overgeslagen
                            auto-bonus hierboven). */}
                        {isChosen && bonuses.some(b => !bonusEffectief(b).isFree) && (
                          <div style={{ marginTop: 8, paddingLeft: 4 }}>
                            {bonuses.filter(b => !bonusEffectief(b).isFree).map((b, idx) => {
                              const { pricePerMonth: bpm } = bonusEffectief(b);
                              const bonusAlreadyActive = activeTypeStartDates[b.bonus_type_id] != null;
                              const isSelected = !bonusAlreadyActive && selectedBonusIds.includes(b.bonus_type_id);
                              return (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: bonusAlreadyActive ? 'not-allowed' : 'pointer', fontSize: 13, opacity: bonusAlreadyActive ? 0.6 : 1 }}
                                  onClick={e => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={bonusAlreadyActive}
                                    onChange={e => {
                                      const newIds = e.target.checked
                                        ? [...selectedBonusIds, b.bonus_type_id]
                                        : selectedBonusIds.filter(id => id !== b.bonus_type_id);
                                      setTypeConfig(prev => ({
                                        ...prev,
                                        [type.id]: { ...prev[type.id], selectedBonusIds: newIds },
                                      }));
                                    }}
                                    style={{ width: 15, height: 15, accentColor: '#1d9e75', cursor: bonusAlreadyActive ? 'not-allowed' : 'pointer' }}
                                  />
                                  <span>
                                    {t('overeenkomst.bonusVoegToe')}: <strong>{b.bonus_type_name}</strong> —{' '}
                                    {bonusAlreadyActive ? alActiefSindsLabel(b.bonus_type_id) : `${euro(bpm)}/mnd`}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Onderdeel 3: uitgesloten types — subtiel, met ongedaan-maken */}
        {coachMode && excludedTypeIds.length > 0 && (
          <div style={{ marginBottom: 24, padding: '10px 14px', background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {t('overeenkomst.uitgeslotenSectieTitel')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {excludedTypeIds.map(tid => {
                const excludedType = membershipTypes.find(mt => mt.id === tid);
                if (!excludedType) return null;
                return (
                  <span key={tid} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white', border: '1px solid #e5e7eb', borderRadius: 16, padding: '3px 10px', fontSize: 12, color: '#6b7280' }}>
                    {excludedType.name}
                    <button
                      type="button"
                      onClick={() => setExcludedTypeIds(prev => prev.filter(id => id !== tid))}
                      style={{ background: 'none', border: 'none', color: '#1d9e75', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                      {t('overeenkomst.uitgeslotenHerstellen')}
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Onderdeel 1: custom regels (coach-only) */}
        {coachMode && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>
              {t('overeenkomst.customRegelsSectieTitel')}
            </div>
            {customLineItems.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                {customLineItems.map(ci => (
                  <div key={ci.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    padding: '10px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{ci.name}</div>
                      <div style={{ fontSize: 12, color: '#92620a', marginTop: 2 }}>
                        {ci.amount != null && <span>{ci.amount >= 0 ? euro(ci.amount) : `− ${euro(Math.abs(ci.amount))}`} {t('overeenkomst.customRegelInclBtw')}</span>}
                        {ci.amount != null && ci.credit_amount != null && <span> · </span>}
                        {ci.credit_amount != null && <span>+{ci.credit_amount} {customPoolLabel(ci.credit_pool)}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCustomItem(ci.id)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 4 }}
                    >
                      {t('overeenkomst.customRegelVerwijderen')}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={openCustomItemModal}
              style={{
                background: 'white', border: '1px dashed #d1d5db', borderRadius: 8,
                padding: '10px 16px', fontSize: 13, fontWeight: 600, color: '#1d9e75', cursor: 'pointer',
              }}
            >
              {t('overeenkomst.customRegelToevoegen')}
            </button>
          </div>
        )}

        {cdErrors.types && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{cdErrors.types}</p>}
        {draftSendError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{draftSendError}</p>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (chosenTypeIds.length === 0) { setCdErrors({ types: t('overeenkomst.minimaalEenType') }); return; }
              setConfigIdx(0);
              setConfigSubStep('staffel');
              setStep(2);
            }}
            disabled={chosenTypeIds.length === 0}
            style={{
              flex: 1, minWidth: 200,
              background: chosenTypeIds.length > 0 ? '#1d9e75' : '#d1d5db',
              color: 'white', border: 'none', borderRadius: 8, padding: '14px 24px',
              fontSize: 15, fontWeight: 700, cursor: chosenTypeIds.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {t('overeenkomst.stepVolgende')} →
          </button>
          <SendToClientButton />
        </div>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // C/D STAP 2 — Configuratie per type
  // ─────────────────────────────────────────────────────────────
  function renderCDStep2() {
    const currentTypeId = chosenTypeIds[configIdx];
    const currentType   = membershipTypes.find(mt => mt.id === currentTypeId);
    const cfg           = SESSION_CONFIG[currentType?.session_type] || {};
    const staffels      = beschikbareStaffels(currentType?.membership_type_periods);
    const currentCfg    = typeConfig[currentTypeId] || {};
    const activeStaffel = staffels.find(s => s.id === currentCfg.staffelId);

    const voortgang = `Abonnement ${configIdx + 1} van ${chosenTypeIds.length}`;

    function goBack() {
      if (configSubStep === 'betaalfreq') {
        setConfigSubStep('staffel');
      } else if (configIdx > 0) {
        setConfigIdx(configIdx - 1);
        const prevType = membershipTypes.find(mt => mt.id === chosenTypeIds[configIdx - 1]);
        const prevStaffels = beschikbareStaffels(prevType?.membership_type_periods);
        const prevCfg = typeConfig[chosenTypeIds[configIdx - 1]] || {};
        const prevStaffel = prevStaffels.find(s => s.id === prevCfg.staffelId);
        setConfigSubStep(prevStaffel?.payment_frequency === 'both' ? 'betaalfreq' : 'staffel');
      } else if (!coachPrefilled) {
        setStep(1);
      }
    }

    function goNext() {
      if (configSubStep === 'staffel') {
        if (!currentCfg.staffelId) { setCdErrors({ staffel: t('overeenkomst.staffelVerplicht') }); return; }
        if (activeStaffel?.payment_frequency === 'both') {
          setConfigSubStep('betaalfreq');
        } else {
          setTypeConfig(prev => ({
            ...prev,
            [currentTypeId]: { ...prev[currentTypeId], payFreq: activeStaffel?.payment_frequency || 'period' },
          }));
          advanceConfig();
        }
      } else {
        if (!currentCfg.payFreq) { setCdErrors({ payFreq: t('overeenkomstBetaalfreq.verplicht') }); return; }
        advanceConfig();
      }
    }

    function advanceConfig() {
      setCdErrors({});
      if (configIdx + 1 < chosenTypeIds.length) {
        setConfigIdx(configIdx + 1);
        setConfigSubStep('staffel');
      } else {
        setAgreementText(buildAgreementText());
        setStep(3);
      }
    }

    return (
      <>
        <BackBtn onClick={goBack} />

        {/* Voortgang */}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {voortgang}
        </div>

        {/* Type-header */}
        <div style={{
          background: cfg.bg || '#f9fafb',
          border: `1px solid ${cfg.border || '#e5e7eb'}`,
          borderLeft: `4px solid ${cfg.color || '#1d9e75'}`,
          borderRadius: 8, padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ background: cfg.color || '#1d9e75', color: 'white', borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 12 }}>
            {cfg.badge}
          </span>
          <strong style={{ fontSize: 15 }}>{currentType?.name}</strong>
          {coachPrefilled && (
            <span style={{ background: '#e5e7eb', color: '#4b5563', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
              {t('overeenkomst.alGekozenDoorCoach')}
            </span>
          )}
        </div>

        {/* Aangepaste voorwaarden (uitsluitend coach-modus; per hoofdabonnement — bonussen niet) */}
        {coachMode && (() => {
          const defaultStaffel = activeStaffel || staffels[0] || null;
          const defaultPricePerMonth = defaultStaffel?.price_per_month ?? null;
          const defaultCredits = currentType?.credits_per_period ?? null;
          const defaultName = currentType?.name || '';
          return (
            <div style={{ marginBottom: 20, padding: '14px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: currentCfg.isCustom ? 12 : 0 }}>
                <input
                  type="checkbox"
                  checked={!!currentCfg.isCustom}
                  onChange={e => setTypeConfig(prev => ({
                    ...prev,
                    [currentTypeId]: { ...prev[currentTypeId], isCustom: e.target.checked },
                  }))}
                  style={{ width: 15, height: 15, accentColor: '#1d9e75', cursor: 'pointer' }}
                />
                {t('overeenkomst.aangepasteVoorwaardenToggle')}
              </label>
              {currentCfg.isCustom && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>{t('overeenkomst.aangepasteNaamLabel')}</label>
                    <input
                      type="text"
                      value={currentCfg.customName || ''}
                      placeholder={t('overeenkomst.standaardNaamHelper', { naam: defaultName })}
                      onChange={e => setTypeConfig(prev => ({ ...prev, [currentTypeId]: { ...prev[currentTypeId], customName: e.target.value } }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>{t('overeenkomst.aangepastePrijsLabel')}</label>
                    <input
                      type="number" step="0.01" min="0"
                      value={currentCfg.customPrice ?? ''}
                      placeholder={defaultPricePerMonth != null ? t('overeenkomst.standaardPrijsHelper', { prijs: euro(defaultPricePerMonth) }) : undefined}
                      onChange={e => setTypeConfig(prev => ({ ...prev, [currentTypeId]: { ...prev[currentTypeId], customPrice: e.target.value } }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
                    />
                    {defaultPricePerMonth != null && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{t('overeenkomst.standaardPrijsHelper', { prijs: euro(defaultPricePerMonth) })}</div>
                    )}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>{t('overeenkomst.aangepasteCreditsLabel')}</label>
                    <input
                      type="number" step="1" min="0"
                      value={currentCfg.customCredits ?? ''}
                      placeholder={defaultCredits != null ? t('overeenkomst.standaardCreditsHelper', { credits: defaultCredits }) : undefined}
                      onChange={e => setTypeConfig(prev => ({ ...prev, [currentTypeId]: { ...prev[currentTypeId], customCredits: e.target.value } }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' }}
                    />
                    {defaultCredits != null && (
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{t('overeenkomst.standaardCreditsHelper', { credits: defaultCredits })}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {configSubStep === 'staffel' && (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{t('overeenkomst.configuratieStaffelTitel')}</h2>

            {staffels.length === 0 ? (
              <div style={{ padding: 16, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: 14 }}>
                {t('overeenkomst.typeNietGevonden')}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 16 }}>
                {staffels.map(s => (
                  <StaffelKaart
                    key={s.id}
                    staffel={s}
                    selected={currentCfg.staffelId === s.id}
                    onClick={() => {
                      setTypeConfig(prev => ({
                        ...prev,
                        [currentTypeId]: { ...prev[currentTypeId], staffelId: s.id, payFreq: '' },
                      }));
                      setCdErrors({});
                    }}
                  />
                ))}
              </div>
            )}
            {cdErrors.staffel && <p style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{cdErrors.staffel}</p>}
          </>
        )}

        {configSubStep === 'betaalfreq' && activeStaffel && (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{t('overeenkomst.configuratieBetaalTitel')}</h2>
            <BetaalfreqKeuze
              staffel={activeStaffel}
              selectedFreq={currentCfg.payFreq || ''}
              onSelect={freq => {
                setTypeConfig(prev => ({
                  ...prev,
                  [currentTypeId]: { ...prev[currentTypeId], payFreq: freq },
                }));
                setCdErrors({});
              }}
            />
            {cdErrors.payFreq && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 12 }}>{cdErrors.payFreq}</p>}
          </>
        )}

        {draftSendError && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{draftSendError}</p>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
          <button
            onClick={goNext}
            style={{
              flex: 1, minWidth: 200,
              background: '#1d9e75', color: 'white', border: 'none', borderRadius: 8,
              padding: '14px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t('overeenkomst.stepVolgende')} →
          </button>
          <SendToClientButton />
        </div>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // STAP 3 — Overzicht (gedeeld)
  // ─────────────────────────────────────────────────────────────
  function renderStep3() {
    const items = getOverzichtItems();
    const betaaldItems = items.filter(i => !i.isFree && i.pricePerMonth != null);
    const totaal = betaaldItems.reduce((sum, i) => sum + (i.pricePerMonth || 0), 0);
    const hoofdItems  = items.filter(i => !i.isBonus);
    const bonusItems  = items.filter(i => i.isBonus);
    const customItems = getCustomLineItemsForDisplay();
    // Onderdeel 2 (2026-07-28 vervolgsessie): expliciet gekozen types die de
    // cliënt al actief blijkt te hebben — zie isHardConflict hierboven.
    const hardConflicts = items.filter(isHardConflict);

    const fromStep = scenario === 'AB'
      ? (beschikbareStaffels(abType?.membership_type_periods).find(s => s.id === abStaffelId)?.payment_frequency === 'both' ? 2 : 1)
      : 2;

    return (
      <>
        <BackBtn onClick={() => setStep(fromStep)} />
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>{t('overeenkomst.overzichtTitelPagina')}</h2>

        {hardConflicts.length > 0 && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
            padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#991b1b', lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('overeenkomst.alActiefWaarschuwingTitel')}</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {hardConflicts.map((c, idx) => (
                <li key={idx}>{c.naam} — {alActiefSindsLabel(c.membershipTypeId)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Hoofdabonnementen */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            {t('overeenkomst.overzichtHoofdAbo')}
          </div>
          {hoofdItems.map((item, idx) => (
            <OverzichtItem key={idx} {...item} />
          ))}
        </div>

        {/* Bonusabonnementen */}
        {bonusItems.length > 0 && (
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              🎁 {t('overeenkomst.overzichtBonussen')}
            </div>
            {bonusItems.map((item, idx) => (
              <OverzichtItem key={idx} {...item} />
            ))}
          </div>
        )}

        {/* Aanvullende afspraken (custom regels, coach-vastgezet) */}
        {customItems.length > 0 && (
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92620a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {t('overeenkomst.customRegelsSectieTitel')}
            </div>
            {customItems.map((ci, idx) => (
              <div key={ci.id || idx} style={{
                padding: '10px 14px', marginBottom: 8,
                background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{ci.name}</span>
                <span style={{ fontSize: 13, color: '#92620a', textAlign: 'right' }}>
                  {ci.amount != null && <span>{ci.amount >= 0 ? euro(ci.amount) : `− ${euro(Math.abs(ci.amount))}`}</span>}
                  {ci.amount != null && ci.credit_amount != null && <span> · </span>}
                  {ci.credit_amount != null && <span>+{ci.credit_amount} {customPoolLabel(ci.credit_pool)}</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Totaal */}
        {totaal > 0 && (
          <div style={{
            marginTop: 20, padding: '14px 16px',
            background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{t('overeenkomst.overzichtTotaal')}</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: '#1d9e75' }}>{euro(totaal)}/mnd</span>
          </div>
        )}

        {draftSendError && <p style={{ color: '#dc2626', fontSize: 13, marginTop: 20 }}>{draftSendError}</p>}

        {coachMode ? (
          <div style={{ marginTop: 20 }}>
            <SendToClientButton full />
          </div>
        ) : (
          <button
            onClick={() => {
              if (hardConflicts.length > 0) return;
              if (!agreementText) setAgreementText(buildAgreementText());
              setStep(4);
            }}
            disabled={hardConflicts.length > 0}
            style={{
              width: '100%', marginTop: 20,
              background: hardConflicts.length > 0 ? '#d1d5db' : '#1d9e75', color: 'white', border: 'none', borderRadius: 8,
              padding: '14px 24px', fontSize: 15, fontWeight: 700,
              cursor: hardConflicts.length > 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {t('overeenkomst.stepVolgende')} →
          </button>
        )}
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // STAP 4 — Overeenkomst lezen + ondertekenen (gedeeld)
  // ─────────────────────────────────────────────────────────────
  function renderStep4() {
    const items = getOverzichtItems();
    const hardConflicts = items.filter(isHardConflict);
    const betaaldItems = items.filter(i => !i.isFree && i.pricePerMonth != null);
    const totaal = betaaldItems.reduce((sum, i) => sum + (i.pricePerMonth || 0), 0);

    return (
      <>
        <BackBtn onClick={() => setStep(3)} />

        {/* Samenvattingsbanner */}
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 8, padding: '14px 18px', marginBottom: 20,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#15803d', marginBottom: 8 }}>
            {t('overeenkomst.overzichtTitelPagina')}
          </div>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: '#166534', marginBottom: 4 }}>
              <span>
                {item.isBonus ? '🎁 ' : ''}
                {item.naam}
                {item.credits != null && <span style={{ marginLeft: 6, fontSize: 11, color: '#4b8b6f' }}>({t('overeenkomst.creditsPerMaand', { n: item.credits })})</span>}
                {item.isFree && <span style={{ marginLeft: 6, fontSize: 10, background: '#dcfce7', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>GRATIS</span>}
              </span>
              <span>{item.isFree ? '' : item.pricePerMonth != null ? `${euro(item.pricePerMonth)}/mnd` : ''}</span>
            </div>
          ))}
          {totaal > 0 && (
            <div style={{ borderTop: '1px solid #86efac', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#15803d', fontSize: 14 }}>
              <span>Totaal</span>
              <span>{euro(totaal)}/mnd</span>
            </div>
          )}
        </div>

        {/* Onderdeel 2 (2026-07-28 vervolgsessie): waarschuwing vóór
            ondertekenen i.p.v. pas na een mislukte submit-poging — bv. bij
            een race condition, of een draft die de coach eerder samenstelde
            toen dit type nog niet actief was bij de cliënt. Blokkeert het
            ondertekenen: de backend-guard zou dit sowieso hard weigeren. */}
        {hardConflicts.length > 0 && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
            padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#991b1b', lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('overeenkomst.alActiefWaarschuwingTitel')}</div>
            <ul style={{ margin: 0, paddingLeft: 18, marginBottom: 6 }}>
              {hardConflicts.map((c, idx) => (
                <li key={idx}>{c.naam} — {alActiefSindsLabel(c.membershipTypeId)}</li>
              ))}
            </ul>
            <div>{t('overeenkomst.alActiefWaarschuwingUitleg')}</div>
          </div>
        )}

        {/* F4-11: NAW-gegevens — verplicht vóór ondertekenen (nodig voor de
            "wonende te [woonplaats]"-verklaring in de overeenkomsttekst).
            Voorgevuld indien al bekend, corrigeerbaar (bv. na een verhuizing). */}
        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{t('overeenkomst.nawTitel')}</h2>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>{t('overeenkomst.nawUitleg')}</p>
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: '16px 18px', marginBottom: 24,
        }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              {t('overeenkomst.nawLabelNaam')}
            </label>
            <div style={{ fontSize: 14, color: '#111827' }}>{tokenData?.client_name}</div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 3 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {t('overeenkomst.nawLabelStraat')}
              </label>
              <input
                type="text"
                value={naamAdres.street}
                onChange={e => setNaamAdres(p => ({ ...p, street: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {t('overeenkomst.nawLabelHuisnummer')}
              </label>
              <input
                type="text"
                value={naamAdres.house_number}
                onChange={e => setNaamAdres(p => ({ ...p, house_number: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {t('overeenkomst.nawLabelPostcode')}
              </label>
              <input
                type="text"
                value={naamAdres.postal_code}
                onChange={e => setNaamAdres(p => ({ ...p, postal_code: e.target.value }))}
                placeholder="1234 AB"
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {t('overeenkomst.nawLabelPlaats')}
              </label>
              <input
                type="text"
                value={naamAdres.city}
                onChange={e => setNaamAdres(p => ({ ...p, city: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{
            marginTop: 14, whiteSpace: 'pre-line', fontSize: 13, lineHeight: 1.6,
            color: '#374151', background: 'white', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '12px 14px',
          }}>
            {buildNaamAdresVerklaringText({
              klantNaam: tokenData?.client_name,
              klantStraat: naamAdres.street,
              klantHuisnummer: naamAdres.house_number,
              klantPostcode: naamAdres.postal_code,
              klantPlaats: naamAdres.city,
            })}
          </div>
        </div>

        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>{t('overeenkomst.leesOvereenkomst')}</h2>
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
          padding: 20, marginBottom: 28, maxHeight: 360, overflowY: 'auto',
          fontSize: 14, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap',
        }}>
          {agreementText || t('overeenkomst.geenTekst')}
        </div>

        {/* Akkoordcheckbox — verplicht voor ondertekenen */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          background: '#f9fafb', border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '12px 16px', marginBottom: 20,
          cursor: 'pointer', fontSize: 14, lineHeight: 1.5,
        }}>
          <input
            type="checkbox"
            checked={agreementAccepted}
            onChange={e => setAgreementAccepted(e.target.checked)}
            style={{ marginTop: 2, accentColor: '#1d9e75', width: 16, height: 16, flexShrink: 0 }}
          />
          <span style={{ color: '#374151' }}>{t('overeenkomst.akkoordLabel')}</span>
        </label>

        {/* F4-10: betaalmethode-keuze — alleen tonen bij meer dan één optie.
            Eén optie (alleen handmatig) → stil terugvallen op handmatig. */}
        {allowedPaymentMethods.length > 1 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{t('overeenkomstBetaalmethode.titel')}</h2>
            <div style={{ display: 'grid', gap: 10 }}>
              {allowedPaymentMethods.map(m => (
                <label key={m} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: paymentMethod === m ? '#ecfdf5' : '#f9fafb',
                  border: `1px solid ${paymentMethod === m ? '#86efac' : '#e5e7eb'}`,
                  borderRadius: 8, padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                }}>
                  <input
                    type="radio"
                    name="payment-method"
                    checked={paymentMethod === m}
                    onChange={() => setPaymentMethod(m)}
                    style={{ accentColor: '#1d9e75', width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600, color: '#374151' }}>
                    {t(`overeenkomstBetaalmethode.${m}`)}
                  </span>
                </label>
              ))}
            </div>

            {/* F4-10 Stap 4b: IBAN-invoer, alleen zichtbaar bij SEPA. Volledige
                IBAN wordt nergens in onze eigen database opgeslagen — alleen
                de laatste 4 cijfers komen terug via de sign-agreement-respons. */}
            {paymentMethod === 'sepa' && (
              <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  {t('overeenkomstBetaalmethode.ibanLabel')}
                </label>
                <input
                  type="text"
                  value={iban}
                  onChange={e => setIban(e.target.value)}
                  placeholder={t('overeenkomstBetaalmethode.ibanPlaceholder')}
                  style={{
                    width: '100%', padding: '10px 12px', fontSize: 14,
                    border: '1px solid #e5e7eb', borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                />
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  {t('overeenkomstBetaalmethode.ibanUitleg')}
                </p>

                {/* F4-10 SEPA-machtigingsaddendum: preview-tekst, identiek
                    aan de servertwin in sign-agreement/index.ts. Het
                    Machtigingskenmerk is een vaste toelichtingszin (geen
                    ruwe referentie meer zichtbaar voor de klant). Losse
                    checkbox, los van de algemene-voorwaarden-checkbox
                    hierboven. */}
                <div style={{
                  marginTop: 16, whiteSpace: 'pre-line', fontSize: 13, lineHeight: 1.6,
                  color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: 8, padding: '14px 16px', marginBottom: 14,
                }}>
                  {buildSepaMachtigingText({
                    klantNaam: tokenData?.client_name,
                    klantAdres: naamAdres.city,
                    klantIban: iban ? iban.replace(/\s+/g, '').toUpperCase() : '',
                    plaats: sepaCompanySettings.company_city,
                    bedrijfsadres: sepaCompanySettings.company_address,
                    incassantId: sepaCompanySettings.mollie_incassant_id,
                    ondertekendatum: new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }),
                    termijnDagen: sepaCompanySettings.company_payment_term_days,
                  })}
                </div>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: 8, padding: '12px 16px',
                  cursor: 'pointer', fontSize: 14, lineHeight: 1.5,
                }}>
                  <input
                    type="checkbox"
                    checked={sepaMandateAccepted}
                    onChange={e => setSepaMandateAccepted(e.target.checked)}
                    style={{ marginTop: 2, accentColor: '#1d9e75', width: 16, height: 16, flexShrink: 0 }}
                  />
                  <span style={{ color: '#374151' }}>{t('overeenkomstBetaalmethode.sepaMachtigingAkkoordLabel')}</span>
                </label>
              </div>
            )}
          </div>
        )}

        <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{t('overeenkomst.plaatsHandtekening')}</h2>
        <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>{t('overeenkomst.handtekeningUitleg')}</p>
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <canvas
            ref={canvasRef}
            width={640}
            height={200}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{ border: '2px solid #e5e7eb', borderRadius: 8, cursor: 'crosshair', display: 'block', width: '100%', touchAction: 'none' }}
          />
          <button
            onClick={clearSignature}
            style={{
              position: 'absolute', top: 8, right: 8,
              background: 'white', border: '1px solid #e5e7eb', borderRadius: 6,
              padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#666',
            }}
          >
            {t('overeenkomst.wisHandtekening')}
          </button>
        </div>

        {signError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{signError}</p>}

        {(() => {
          const canSign = agreementAccepted && hasSignature && !signing
            && (paymentMethod !== 'sepa' || sepaMandateAccepted)
            && isNaamAdresValid() && hardConflicts.length === 0;
          return (
            <button
              onClick={handleSign}
              disabled={!canSign}
              style={{
                width: '100%',
                background: canSign ? '#1d9e75' : '#d1d5db',
                color: 'white', border: 'none', borderRadius: 8, padding: '16px 24px',
                fontSize: 16, fontWeight: 700,
                cursor: canSign ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s', marginBottom: 12,
              }}
            >
              {signing ? t('overeenkomst.bezig') : t('overeenkomst.ondertekenen')}
            </button>
          );
        })()}

        <button
          onClick={() => setRejectModal(true)}
          style={{
            width: '100%', background: 'transparent', color: '#dc2626',
            border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 24px',
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('overeenkomst.afwijzenKnop')}
        </button>
      </>
    );
  }
}
